import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, MessageCircle, LayoutDashboard, ShieldCheck } from "lucide-react";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { EllieOrb } from "@/components/discovery/EllieOrb";
import { AccountLink } from "@/components/journey/AccountLink";
import { PrimaryButton } from "@/components/fionova/Primitives";
import { useAuth } from "@/context/AuthContext";
import { discover } from "@/lib/discover";
import { track, trackPersistentOnce } from "@/lib/analytics";

const NEXT = [
  { key: "journey", icon: Compass, title: "Explore the Full Journey", sub: "The deeper 10–15 minute guided experience with Ellie.", to: "/journey" },
  { key: "chat", icon: MessageCircle, title: "Talk With Ellie", sub: "A few minutes exploring one of these insights together.", to: "/app?chat=1" },
  { key: "dashboard", icon: LayoutDashboard, title: "Go to My Dashboard", sub: "Everything in your subscription, unlocked.", to: "/app" },
];

const Insight = ({ label, data, i, testId }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.25, duration: 0.6 }}
    className="fio-glass-strong rounded-[28px] p-6" data-testid={testId}>
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-teal-700">{label}</p>
    <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">{data.title}</h2>
    <p className="mt-2 text-[15.5px] leading-relaxed text-slate-700">{data.text}</p>
  </motion.div>
);

const Reveal = ({ r, navigate }) => (
  <div className="space-y-5" data-testid="discover-reveal">
    <div className="flex flex-col items-center text-center">
      <EllieOrb size={80} />
      <p className="mt-5 text-lg font-medium text-slate-900">Now that we've had a chance to connect, here's what stood out to me.</p>
    </div>
    <Insight label="The Unsaid" data={r.unsaid} i={0} testId="discover-insight-unsaid" />
    <Insight label="Emotional X-Ray" data={r.xray} i={1} testId="discover-insight-xray" />
    {r.reflection && <p className="px-2 text-center text-[15px] italic leading-relaxed text-slate-600" data-testid="discover-reflection">{r.reflection}</p>}
    <div className="space-y-3 pt-2">
      {NEXT.map(({ key, icon: Icon, title, sub, to }) => (
        <button key={key} data-testid={`discover-next-${key}`} onClick={() => { track("discover_next_step_selected", { step: key }); window.scrollTo(0, 0); navigate(to); }}
          className="flex w-full items-center gap-4 rounded-2xl bg-white p-4 text-left ring-1 ring-slate-200 transition-shadow hover:ring-teal-400">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-600/10 text-teal-700"><Icon className="h-5 w-5" /></span>
          <span><span className="block font-semibold text-slate-900">{title}</span><span className="block text-sm text-slate-600">{sub}</span></span>
        </button>
      ))}
    </div>
  </div>
);

// After Stripe: verify payment server-side, create or link ONE account, then reveal.
export default function DiscoverUnlock() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refresh, logout } = useAuth();
  const did = params.get("d") || discover.id();
  const sid = params.get("session_id");
  const [phase, setPhase] = useState("checking");
  const [info, setInfo] = useState(null);
  const [r, setR] = useState(null);
  const started = useRef(false);

  const show = async () => {
    const res = await discover.results(did);
    setR(res); setPhase("reveal"); discover.clear();
    trackPersistentOnce(`discover_unlocked:${did}`, "discover_results_unlocked");
  };

  const linkAndShow = async () => {
    if (sid) {
      await discover.link(did, sid);
      trackPersistentOnce(`discover_purchase:${sid}`, "discover_purchase_completed", { transaction_id: sid, value: 2.99, currency: "USD" });
      await refresh();
    }
    await show();
  };

  const check = async (tries = 0) => {
    if (!did) return setPhase("missing");
    if (user) {
      try { return await linkAndShow(); } catch (e) { if (!sid || e.status !== 402) return setPhase(e.status === 403 ? "other" : "missing"); }
    }
    if (!sid) return setPhase(user ? "missing" : "signin");
    const v = await discover.verify(did, sid).catch(() => ({ paid: false }));
    if (v.paid && v.claimed) return setPhase("signin");
    if (v.paid) { setInfo(v); return setPhase("account"); }
    if (tries < 8) return setTimeout(() => check(tries + 1), 2500);
    setPhase("unpaid");
  };

  useEffect(() => {
    if (user === undefined || started.current) return;
    started.current = true;
    check();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OnboardingLayout testId="page-discover-unlock" contentClassName="py-8">
      <div className="mx-auto w-full max-w-md">
        {phase === "checking" && <div className="flex flex-col items-center py-16 text-center" data-testid="discover-confirming"><EllieOrb size={88} /><p className="mt-6 text-slate-600">Confirming your payment securely…</p></div>}
        {(phase === "account" || phase === "signin") && (
          <div className="space-y-4" data-testid="discover-account">
            <p className="flex items-center gap-2 text-sm font-medium text-teal-700"><ShieldCheck className="h-4 w-4" /> {phase === "account" ? "Payment confirmed" : "Sign in to see your insights"}</p>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{phase === "account" ? "One last step: secure your account" : "Welcome back"}</h1>
            <p className="text-[15px] text-slate-600">Your two insights and your subscription are saved to this account, so you'll never need to answer again.</p>
            <AccountLink initialMode={info?.account_exists || phase === "signin" ? "signin" : "create"} email={info?.email || ""} lockEmail={!!info?.email && !info?.account_exists}
              footnote="Your Discover answers, insights and subscription move into this one account." onDone={() => linkAndShow().catch(() => setPhase("other"))} />
          </div>
        )}
        {phase === "reveal" && r && <Reveal r={r} navigate={navigate} />}
        {phase === "unpaid" && (
          <div className="space-y-4 py-10 text-center" data-testid="discover-unpaid">
            <p className="text-slate-700">We couldn't confirm a completed payment yet, so nothing has been unlocked. If you were charged, refresh in a minute.</p>
            <PrimaryButton data-testid="discover-unpaid-retry" onClick={() => { setPhase("checking"); check(); }}>Check again</PrimaryButton>
            <button className="text-sm text-slate-500 underline" onClick={() => navigate(`/discover?d=${did}`)} data-testid="discover-unpaid-back">Back to my results</button>
          </div>
        )}
        {(phase === "missing" || phase === "other") && (
          <div className="space-y-4 py-10 text-center" data-testid="discover-missing">
            <p className="text-slate-700">{phase === "other" ? "These insights belong to a different account. Sign in with the account that purchased them." : "We couldn't find these results on this account."}</p>
            <PrimaryButton onClick={() => navigate(user?.entitled ? "/app" : "/discover")}>{user?.entitled ? "Go to my dashboard" : "Back to Discover"}</PrimaryButton>
            {phase === "other" && <button data-testid="discover-switch-account" className="text-sm text-slate-500 underline" onClick={() => { logout(); window.location.reload(); }}>Sign out and use another account</button>}
          </div>
        )}
      </div>
    </OnboardingLayout>
  );
}
